import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FieldTree, FormField, SchemaPathTree, min } from '@angular/forms/signals';
import { InDto, MCB_IEC60898Dto, MceApiService } from '../../../../core/mceapi.client.generated';
import { match, none, Option, some } from '../../../../shared/functions/functional';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Load } from '../../load/load.model';
import { of } from 'rxjs';

export interface MCBIEC60898 {
  assemblyGuid: string;
  rating: number;
  autoRating: boolean;
}

export function buildMCBIEC60898Schema(path: SchemaPathTree<MCBIEC60898>) {}

export function createMCBIEC60898(dto: Option<MCB_IEC60898Dto>) {
  return signal<MCBIEC60898>(
    match(
      () => ({ assemblyGuid: '', rating: 0, autoRating: true }),
      (x: MCB_IEC60898Dto): MCBIEC60898 => {
        if (x.assemblyGuid === undefined || x.rating === undefined || x.autoRating === undefined) {
          throw new Error('MCB DTO is missing required values.');
        }

        return {
          assemblyGuid: x.assemblyGuid,
          rating: x.rating,
          autoRating: x.autoRating
        };
      })(dto));
}


export function createMCBIEC60898Dto(model: FieldTree<MCBIEC60898>) {
  return signal<Option<MCB_IEC60898Dto>>(
    model.assemblyGuid().value() === '' || model.rating().value() === 0
      ? none
      : some({
          assemblyGuid: model.assemblyGuid().value(),
          rating: model.rating().value(),
          autoRating: model.autoRating().value()
        }));
      }



@Component({
  selector: 'protection-mcb',
  imports: [FormField, MatSelectModule, MatSlideToggleModule],
  templateUrl: './mcb.component.html',
})
export class McbComponent {

  readonly form = input.required<FieldTree<MCBIEC60898>>();
  readonly load = input.required<FieldTree<Load>>();

  private readonly mceApiService = inject(MceApiService);

  framesRx = rxResource({
    params: () => ({}),
    stream: () =>
      this.mceApiService.v1ProtectionFrames('MCB_IEC60898')
  });
  frames = computed(() => this.framesRx.value() ?? []);

  ratingsRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of([])
        : this.mceApiService.v1ProtectionRatings('MCB_IEC60898', params.assemblyGuid)
  });
  ratings = computed(() => this.ratingsRx.value() ?? []);

  autoRatingRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value(), ib: this.load().ib().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of({ i: 0 })
      : this.mceApiService.v1ProtectionRatingsAuto('MCB_IEC60898', params.assemblyGuid, params.ib)
  });
  autoRating = computed<InDto>(() => this.autoRatingRx.value() ?? { i: 0 });

  setAutoRating = effect(() => {
    if (!this.form().autoRating().value()) return;
    const autoRating = this.autoRating().i;
    if (autoRating === undefined || autoRating === 0) return;

    if (autoRating !== this.form().rating().value())
      this.form().rating().value.set(autoRating);
  });


  /// Ensure the frames has a valid selection.
  frameToValid = effect(() => {
    const frameGuid = this.frames()[1]?.value;
    if (frameGuid !== undefined && this.form().assemblyGuid().value() === '')
      this.form().assemblyGuid().value.set(frameGuid);
  });



}
